import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { SegmentMeterProps } from "./SegmentMeter.types";

// How close to a segment boundary before the counter turns red.
const NEAR_BOUNDARY = 15;

/**
 * Encoding / segment / character readout for an SMS body. Every number comes
 * from the backend segment calculator.
 */
function SegmentMeter(props: SegmentMeterProps) {
  const { calculation, loading } = props;

  const segments = calculation?.segments ?? 0;
  const used = calculation?.characterCount ?? 0;
  const remaining = calculation?.charactersRemainingInSegment ?? 0;
  const capacity = used + remaining;
  const perSegment = segments > 0 ? capacity / segments : 0;
  const fillPercent =
    perSegment > 0 ? ((perSegment - remaining) / perSegment) * 100 : 0;
  const nearBoundary = used > 0 && remaining <= NEAR_BOUNDARY;
  const isUnicode = calculation?.encoding === "UCS2";
  const offenders = calculation?.nonGsmCharacters ?? [];

  return (
    <Box sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 150ms" }}>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap="12px" mt={2}>
        {calculation && (
          <Chip
            label={isUnicode ? "Unicode" : "GSM-7"}
            size="small"
            color={isUnicode ? "secondary" : "primary"}
          />
        )}
        <Typography fontSize={13} color="text.secondary">
          {segments} segment{segments === 1 ? "" : "s"}
        </Typography>
        {capacity > 0 && (
          <Typography
            fontSize={13}
            color={nearBoundary ? "error" : "text.secondary"}
            fontWeight={nearBoundary ? 700 : undefined}
          >
            {used} / {capacity}
          </Typography>
        )}
      </Box>

      <Box
        sx={(theme) => ({
          width: "100%",
          height: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.grey[200],
          mt: 2,
          overflow: "hidden",
        })}
      >
        <Box
          sx={(theme) => ({
            height: "100%",
            width: `${fillPercent}%`,
            backgroundColor: theme.palette.primary.main,
            transition: "width 150ms ease",
          })}
        />
      </Box>

      {offenders.length > 0 && (
        <Typography fontSize={13} color="text.secondary" mt={2}>
          {"Switched to Unicode by "}
          {offenders.map((char) => `“${char}”`).join(", ")}
          {" — swap these out to fit more text in each segment."}
        </Typography>
      )}
      {calculation?.containsMergeFields && (
        <Typography fontSize={13} color="text.secondary" mt={1}>
          Merge fields are counted as written, so some contacts&apos; messages
          may run longer.
        </Typography>
      )}
    </Box>
  );
}

export default SegmentMeter;
